/**
 * @file Polarity.jsx
 * @description Polarity component for displaying customer sentiment data.
 * @component Polarity
 * @exports Polarity - Main component for displaying customer sentiment data.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, IconButton, Typography
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Area, AreaChart, CartesianGrid, Legend,
  ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import {
  fetchPolarityData,
  fetchPolarityConversations,
} from "../../../services/insightModule/insightService.js";
import DatePickerComponent from "../../../common/components/DatePickerComponent.jsx";
import ConversationDrillDownDialog from "../../../components/ConversationDrillDownDialog";
import { STRINGS } from "../../../common/constants/commonConstants.js";
import { formatDateTime } from "../../../common/constants/insightModule/polarityConstants.js";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const GREEN  = "#1D9E75";
const AMBER  = "#BA7517";
const PINK   = "#D4537E";
const RED    = "#A32D2D";
const TEAL   = "#85B7EB";

const POLARITY_KEYS = ["VeryPositive", "Positive", "Neutral", "Negative", "VeryNegative"];

const POLARITY_COLORS = {
  VeryPositive: GREEN,
  Positive:     TEAL,
  Neutral:      PINK,
  Negative:     AMBER,
  VeryNegative: RED,
};

const POLARITY_LABELS = {
  VeryPositive: "Very Positive",
  Positive:     "Positive",
  Neutral:      "Neutral",
  Negative:     "Negative",
  VeryNegative: "Very Negative",
};

// ── Helpers ────────────────────────────────────────────────────────
function parseLocalDate(val) {
  if (!val) return null;
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
}

function fmtDate(val) {
  const d = parseLocalDate(val);
  if (!d || isNaN(d)) return val || "";
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

function safeNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

// ── Sub-components ─────────────────────────────────────────────────
function KpiCard({ label, value, color = BLUE, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        background: "#f7f7f7", borderRadius: 2,
        p: "10px 14px", minWidth: 0, flex: 1,
        cursor: onClick ? "pointer" : "default",
        transition: "background .15s",
        "&:hover": onClick ? { background: "#efefef" } : {},
      }}
    >
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, children, height = 200 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────────
const Polarity = () => {
  const [startDate, setStartDate] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [endDate,   setEndDate]   = useState(() => new Date());
  const [channel,   setChannel]   = useState("Texts");
  const [data,      setData]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [page,      setPage]      = useState(0);
  const [drill,     setDrill]     = useState(null);
  const rowsPerPage = 25;

  const load = useCallback(async (start, end, ch) => {
    setLoading(true);
    setError("");
    setPage(0);
    try {
      const result = await fetchPolarityData({
        startDate:    formatDateTime(start),
        endDate:      formatDateTime(end),
        polarityType: ch,
      });
      setData(result || []);
    } catch (e) {
      console.error(e);
      setError("Failed to load polarity data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(startDate, endDate, "Texts");
    // load once on mount; date defaults are stable via lazy initializer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── KPIs ───────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total       = data.reduce((a,b) => a + safeNum(b.Total),       0);
    const veryPos     = data.reduce((a,b) => a + safeNum(b.VeryPositive), 0);
    const pos         = data.reduce((a,b) => a + safeNum(b.Positive),     0);
    const neutral     = data.reduce((a,b) => a + safeNum(b.Neutral),      0);
    const neg         = data.reduce((a,b) => a + safeNum(b.Negative),     0);
    const veryNeg     = data.reduce((a,b) => a + safeNum(b.VeryNegative), 0);
    return {
      total, veryPos, pos, neutral, neg, veryNeg,
      veryPosPct: total > 0 ? veryPos / total * 100 : 0,
      posPct:     total > 0 ? pos     / total * 100 : 0,
      neuPct:     total > 0 ? neutral / total * 100 : 0,
      negPct:     total > 0 ? neg     / total * 100 : 0,
      veryNegPct: total > 0 ? veryNeg / total * 100 : 0,
    };
  }, [data]);

  // ── Chart data ─────────────────────────────────────────────────
  // _convDate kept on each point so the chart click can drill the date
  const chartData = useMemo(() =>
    [...data]
      .sort((a, b) => {
        const da = parseLocalDate(a.ConvDate);
        const db = parseLocalDate(b.ConvDate);
        return (da?.getTime() || 0) - (db?.getTime() || 0);
      })
      .map(r => ({
        date:        fmtDate(r.ConvDate),
        _convDate:   r.ConvDate,
        VeryPositive: safeNum(r.VeryPositive),
        Positive:     safeNum(r.Positive),
        Neutral:      safeNum(r.Neutral),
        Negative:     safeNum(r.Negative),
        VeryNegative: safeNum(r.VeryNegative),
      }))
  , [data]);

  // ── Sorted table data with pagination ─────────────────────────
  const sortedData = useMemo(() =>
    [...data].sort((a, b) => {
      const da = parseLocalDate(a.ConvDate);
      const db = parseLocalDate(b.ConvDate);
      return (db?.getTime() || 0) - (da?.getTime() || 0);
    })
  , [data]);

  const pagedData = useMemo(() =>
    sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  , [sortedData, page]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  // ── Drill-down ─────────────────────────────────────────────────
  // polarity only       → all conversations w/ that bucket in the picker range
  // date only           → all conversations on that date (any polarity)
  // polarity + date     → that bucket on that single date
  const openDrill = useCallback(({ polarity = null, date = null }) => {
    const polarityColor = polarity ? POLARITY_COLORS[polarity] : null;
    const polarityLabel = polarity ? POLARITY_LABELS[polarity] : null;

    const title =
      polarity && date ? "Conversations by polarity & date" :
      polarity         ? `Conversations with polarity: ${polarityLabel}` :
                         `Conversations on ${fmtDate(date)}`;

    const dStart = date ? new Date(date) : startDate;
    const dEnd   = date ? new Date(date) : endDate;

    setDrill({
      title,
      metric: polarity ? { label: "Polarity", value: polarityLabel, color: polarityColor } : null,
      context: [
        date && { label: "Date", value: fmtDate(date) },
        !date && startDate && { label: "From", value: fmtDate(startDate) },
        !date && endDate   && { label: "To",   value: fmtDate(endDate) },
      ].filter(Boolean),
      channel,
      // Map human-readable labels (which is what description contains in DB)
      // to colors so the chip on each conversation card is color-matched.
      emotionColors: Object.fromEntries(
        POLARITY_KEYS.map(k => [POLARITY_LABELS[k], POLARITY_COLORS[k]])
      ),
      fetchData: () => fetchPolarityConversations({
        polarity,
        startDate:   dStart ? dStart.toISOString() : null,
        endDate:     dEnd   ? dEnd.toISOString()   : null,
        channeltype: channel,
      }),
    });
  }, [startDate, endDate, channel]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };

  // 40px drill icon column added at the start
  const COLS    = "40px 100px 110px 110px 110px 110px 120px 80px";
  const HEADERS = ["", "Date", "Very Positive", "Positive", "Neutral", "Negative", "Very Negative", "Total"];

  // Per-cell color/key mapping for the count columns
  const CELL_COLS = [
    { key: "VeryPositive", color: GREEN },
    { key: "Positive",     color: TEAL  },
    { key: "Neutral",      color: PINK  },
    { key: "Negative",     color: AMBER },
    { key: "VeryNegative", color: RED   },
  ];

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Filters ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <DatePickerComponent
          label={STRINGS.START_DATE}
          date={startDate}
          onDateChange={setStartDate}
          marginRight={0}
        />
        <DatePickerComponent
          label={STRINGS.END_DATE}
          date={endDate}
          onDateChange={d => { if (d >= startDate) setEndDate(d); }}
          marginRight={0}
        />
        <Box
          onClick={() => load(startDate, endDate, channel)}
          sx={{
            px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            background: BLUE, color: "#fff", fontWeight: 500,
            alignSelf: "flex-end", mb: "1px",
            "&:hover": { background: "#2a6db5" },
          }}
        >
          Search
        </Box>

        {/* Channel toggle */}
        <Box sx={{
          display: "flex", ml: "auto",
          border: "0.5px solid", borderColor: "divider",
          borderRadius: "20px", overflow: "hidden",
          alignSelf: "flex-end",
        }}>
          {["Texts", "Emails"].map(ch => (
            <Box key={ch} onClick={() => {
              setChannel(ch);
              load(startDate, endDate, ch);
            }} sx={{
              px: "14px", py: "5px", fontSize: 13,
              fontWeight:  channel === ch ? 500 : 400,
              cursor: "pointer", userSelect: "none",
              background:  channel === ch ? BLUE : "transparent",
              color:       channel === ch ? "#fff" : "text.secondary",
              transition:  "all .15s",
            }}>
              {ch}
            </Box>
          ))}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loading && data.length === 0 && !error && (
        <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
          No data for selected range.
        </Typography>
      )}

      {!loading && data.length > 0 && (
        <>
          {/* ── SUMMARY KPIs (clickable for polarity-only drill) ── */}
          <SectionTitle>Summary</SectionTitle>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
            <KpiCard label="Total records"  value={kpis.total.toLocaleString()}      color={BLUE}  />
            <KpiCard label="Very positive"  value={`${kpis.veryPosPct.toFixed(1)}%`} color={GREEN}
              onClick={() => openDrill({ polarity: "VeryPositive" })} />
            <KpiCard label="Positive"       value={`${kpis.posPct.toFixed(1)}%`}     color={TEAL}
              onClick={() => openDrill({ polarity: "Positive" })} />
            <KpiCard label="Neutral"        value={`${kpis.neuPct.toFixed(1)}%`}     color={PINK}
              onClick={() => openDrill({ polarity: "Neutral" })} />
            <KpiCard label="Negative"       value={`${kpis.negPct.toFixed(1)}%`}     color={AMBER}
              onClick={() => openDrill({ polarity: "Negative" })} />
            <KpiCard label="Very negative"  value={`${kpis.veryNegPct.toFixed(1)}%`} color={RED}
              onClick={() => openDrill({ polarity: "VeryNegative" })} />
          </Box>

          {/* ── STACKED AREA CHART (click a point to drill that date) ── */}
          <SectionTitle>Polarity over time</SectionTitle>

          <ChartCard height={280}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                style={{ cursor: "pointer" }}
                onClick={(state) => {
                  const dt = state?.activePayload?.[0]?.payload?._convDate;
                  if (dt) openDrill({ date: dt });
                }}
              >
                <defs>
                  {Object.entries(POLARITY_COLORS).map(([key, color]) => (
                    <linearGradient key={key} id={`grad_${key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={color} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={color} stopOpacity={0.4} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis tick={axisStyle} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                {/* Bottom to top stacking */}
                {["VeryNegative","Negative","Neutral","Positive","VeryPositive"].map(key => (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={POLARITY_LABELS[key]}
                    stackId="polarity"
                    stroke={POLARITY_COLORS[key]}
                    fill={`url(#grad_${key})`}
                    strokeWidth={1}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── DATA TABLE ────────────────────────────────────── */}
          <SectionTitle>Detail</SectionTitle>

          <Box sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, overflow: "hidden",
          }}>
            {/* Header */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: COLS,
              background: "#f7f7f7",
              borderBottom: "0.5px solid", borderColor: "divider",
              px: 2, py: 1,
              fontSize: 11, fontWeight: 500,
              color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.05em",
              alignItems: "center",
            }}>
              {HEADERS.map((h, i) => (
                <Box key={i} sx={{ textAlign: i <= 1 ? "left" : "right" }}>{h}</Box>
              ))}
            </Box>

            {/* Rows */}
            {pagedData.map((row, i) => {
              const total = safeNum(row.Total) || 1;
              return (
                <Box key={i} sx={{
                  display: "grid",
                  gridTemplateColumns: COLS,
                  px: 2, py: "6px",
                  fontSize: 12,
                  borderBottom: "0.5px solid", borderColor: "divider",
                  background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                  "&:hover": { background: "rgba(55,138,221,0.06)" },
                  alignItems: "center",
                }}>
                  {/* drill all polarities for this date */}
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => openDrill({ date: row.ConvDate })}
                      sx={{ width: 24, height: 24 }}
                      title="View conversations for this date"
                    >
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                  <Box>{fmtDate(row.ConvDate)}</Box>
                  {CELL_COLS.map(({ key, color }) => {
                    const count = safeNum(row[key]);
                    const clickable = count > 0;
                    return (
                      <Box
                        key={key}
                        onClick={clickable
                          ? () => openDrill({ polarity: key, date: row.ConvDate })
                          : undefined}
                        sx={{
                          textAlign: "right",
                          color: clickable ? color : "text.disabled",
                          fontWeight: clickable && key === "VeryPositive" ? 500 : 400,
                          cursor: clickable ? "pointer" : "default",
                          "&:hover": clickable ? { textDecoration: "underline" } : {},
                        }}
                      >
                        {count}
                        <Box component="span" sx={{ fontSize: 10, color: "text.secondary", ml: 0.5 }}>
                          ({(count/total*100).toFixed(0)}%)
                        </Box>
                      </Box>
                    );
                  })}
                  <Box sx={{ textAlign: "right", fontWeight: 500 }}>
                    {safeNum(row.Total).toLocaleString()}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{
              display: "flex", justifyContent: "flex-end",
              alignItems: "center", gap: 1, mt: 1, fontSize: 12,
              color: "text.secondary",
            }}>
              <Box
                onClick={() => setPage(p => Math.max(0, p - 1))}
                sx={{
                  px: "10px", py: "3px", borderRadius: "20px",
                  border: "0.5px solid", borderColor: "divider",
                  cursor: page === 0 ? "default" : "pointer",
                  opacity: page === 0 ? 0.4 : 1,
                  "&:hover": { borderColor: page === 0 ? "divider" : BLUE },
                }}
              >
                ‹
              </Box>
              <span>Page {page + 1} of {totalPages}</span>
              <Box
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                sx={{
                  px: "10px", py: "3px", borderRadius: "20px",
                  border: "0.5px solid", borderColor: "divider",
                  cursor: page >= totalPages - 1 ? "default" : "pointer",
                  opacity: page >= totalPages - 1 ? 0.4 : 1,
                  "&:hover": { borderColor: page >= totalPages - 1 ? "divider" : BLUE },
                }}
              >
                ›
              </Box>
            </Box>
          )}
        </>
      )}

      {/* ── Drill-down popup ───────────────────────────────────── */}
      {drill && (
        <ConversationDrillDownDialog
          open={!!drill}
          onClose={() => setDrill(null)}
          {...drill}
        />
      )}
    </Box>
  );
};

export default Polarity;